/**
 * Application Configuration
 * Central place for app-wide configuration values
 */

import { getReleaseIdentity } from './buildIdentity';

// Application version information
export const APP_VERSION = getReleaseIdentity().version;

// Update history for the application
export const UPDATE_HISTORY = [
  {
    date: "28/08/26",
    version: "5.10.5",
    changes: [
      "Prepared the browser and Edge Functions for Supabase publishable and secret API keys.",
      "Kept user JWT authorization separate from API key headers and tightened privileged MFA diagnostics."
    ]
  },
  {
    date: "26/08/26",
    version: "5.10.3",
    changes: [
      "Added secure pre-shell TOTP enrollment for accounts without an MFA factor.",
      "Kept protected content locked until the enrolled session is rechecked at AAL2."
    ]
  },
  {
    date: "26/08/26",
    version: "5.10.2",
    changes: [
      "Made the protected shell fail closed until a valid AAL2 assurance check succeeds.",
      "Removed the named password-only bypass from MFA login verification."
    ]
  },
  {
    date: "24/08/26",
    version: "5.10.0",
    changes: [
      "Added traceable Alpha build identity in the sidebar."
    ]
  },
  {
    date: "21/08/25",
    version: "5.9.1",
    changes: [
      "🔧 Executive Summary Report Fix: Corrected maturity level progression logic in Maturity Level Summary section",
      "🟠 Progress Display Fix: ML2 and ML3 columns now properly show orange (in progress) when controls are being worked on, regardless of prerequisite completion",
      "🔵 Current Level Logic Fix: Blue circle indicator now correctly shows only the highest completed incremental maturity level",
      "⚡ Enhanced Progress Calculation: Added calculateRawProgress() function to separate visual progress display from achievement logic",
      "📊 Improved UX: Users can now see progress on higher maturity levels while maintaining proper incremental achievement requirements",
      "✅ Business Logic Compliance: Maintains ML1 → ML2 → ML3 incremental progression for level achievement while showing all active work"
    ]
  },
  {
    date: "15/08/25",
    version: "5.9.0",
    changes: [
      "🗂️ Major Feature: E8 Tasks Management System - Comprehensive task management for Essential Eight controls",
      "📋 Task Dashboard: Full-featured dashboard with list view, filtering, search, and task statistics",
      "🎯 Control Integration: Fourth tab added to E8 control modal for control-specific task management",
      "💾 Database Foundation: Complete Supabase schema with tasks, comments, attachments, and audit trails",
      "🔐 Secure API: 5 new Edge Functions with Row Level Security for task CRUD operations",
      "📊 Smart Statistics: Real-time task analytics with priority, status, and completion tracking",
      "⚡ Performance Optimized: TaskService with intelligent caching and batch operations",
      "🔄 Task Lifecycle: Complete status management from creation to completion with audit history",
      "🎨 Rich UI Components: TaskList, TaskFilters, TaskStatsCards with responsive design",
      "🛡️ Permission System: Module-based access control integrated with existing permission framework"
    ]
  },
  {
    date: "13/01/25",
    version: "5.8.0",
    changes: [
      "🎨 Major UI Enhancement: E8 Controls Modal Tabbed Interface - Implemented comprehensive tabbed system to eliminate excessive scrolling",
      "📑 Three-Tab Organization: Overview (Control & methodology), Assessment (Testing & analysis), Recommendations (Improvement actions)",
      "🖥️ Enlarged Modal Size: Increased from max-w-4xl to max-w-6xl for better workspace utilization",
      "⌨️ Keyboard Navigation: Added Ctrl/Cmd + 1/2/3 shortcuts for quick tab switching within modal",
      "🔄 Smart Progress Indicators: Visual completion status badges show green (complete), yellow (in progress), or no indicator (empty)",
      "💾 Enhanced Auto-save: Triggers save when switching tabs to prevent data loss during navigation",
      "✨ Smooth Animations: Added fade-in transitions for tab content switching with 300ms duration",
      "📱 Collapsible Quick Reference: Assessment tab includes collapsible methodology reference for easy access",
      "📊 Summary Insights Panel: Recommendations tab displays comprehensive status overview with completion metrics",
      "🎯 Improved UX: Eliminated 80% of scrolling while maintaining all existing functionality and data integrity"
    ]
  },
  {
    date: "12/08/25",
    version: "5.7.4",
    changes: [
      "🗄️ Major: Test Methodology Migration - Migrated from static files to Supabase database system",
      "🔐 Secure Edge Functions - Implemented authenticated get-methodology API with Bearer token validation",
      "💾 Database Foundation - Created default_methodologies and user_methodologies tables with RLS policies", 
      "⚡ Smart Caching - Added methodologyService with intelligent caching and fallback handling",
      "🛡️ Enhanced Security - All methodology access secured with JWT authentication and service role bypass",
      "🔄 Seamless Migration - Maintained backward compatibility while transitioning to scalable database architecture"
    ]
  },
  {
    date: "12/08/25",
    version: "5.7.3",
    changes: [
      "🐛 Fixed Final Character Deletion Issue - Users can now delete ALL text including the very last character",
      "✅ Complete Text Clearing - Text areas can be completely emptied without leaving stuck characters",
      "🔧 Improved Empty String Handling - Fixed condition to properly handle empty fieldValue",
      "📝 Enhanced Text Deletion - No more undeletable characters at the beginning of text fields",
      "🎯 Perfect Text Control - Full typing, editing, and deletion capabilities restored"
    ]
  },
  {
    date: "12/08/25",
    version: "5.7.2",
    changes: [
      "🔧 Fixed Text Input Functionality - Text areas now work completely for typing and deleting",
      "📝 Restored Full Text Editing - Test Findings, Justification, and Recommendations fully functional",
      "🐛 Corrected Parameter Order - Fixed onChange event handler to use proper 4-parameter format",
      "✅ Complete Text Operations - Users can now type, edit, and delete all text including last character",
      "🎯 Enhanced User Experience - All textarea fields respond properly to user input"
    ]
  },
  {
    date: "12/08/25",
    version: "5.7.1",
    changes: [
      "🐛 Fixed Text Deletion Bug - Text areas now properly delete all characters including the last one",
      "📝 Enhanced Text Input - Test Findings, Justification, and Recommendations fields work correctly",
      "🔧 Improved Form Handling - Fixed onChange event handling for all textarea fields",
      "🖥️ Cleaner Login Screen - Removed version information box from login page",
      "🎨 Streamlined UI - Login screen now has cleaner appearance without version overlay"
    ]
  },
  {
    date: "12/08/25",
    version: "5.7.0",
    changes: [
      "🌐 Global App State Persistence - Complete application state now persists across all browser tab navigation",
      "📍 Universal Navigation Memory - Sidebar module selection (E8 Tracker, Reports, Crown Jewels, etc.) preserved across tabs",
      "🎯 Comprehensive Position Tracking - Active control, question modals, and all navigation states maintained globally",
      "💾 Centralized State Management - New AppStateContext provides unified state persistence for the entire application",
      "🔄 Complete Session Restoration - Users maintain exact position anywhere in the app when returning from other tabs",
      "🛠️ Enhanced Debugging - Global state debug functions available for troubleshooting and monitoring"
    ]
  },
  {
    date: "12/08/25",
    version: "5.6.2",
    changes: [
      "🎯 Fixed Question Modal Persistence - Question editing panels now stay open when navigating between browser tabs",
      "📋 Enhanced Modal State Management - selectedQuestion and isPanelOpen states persist to localStorage",
      "🔄 Seamless Editing Experience - Users can continue editing specific questions exactly where they left off", 
      "💾 Complete Session Continuity - Question modals, panel state, and form progress all preserved across tab navigation",
      "🛠️ Smart State Validation - Panel only reopens if the question still exists in current assessment"
    ]
  },
  {
    date: "12/08/25",
    version: "5.6.1",
    changes: [
      "🔄 Enhanced Session Persistence - Complete assessment state now persists across browser tabs",
      "📍 Full State Management - activeTab, maturityLevel, and assessmentName all persist to localStorage",
      "🎯 Improved User Experience - Users maintain exact position and progress when returning from other tabs", 
      "💾 Comprehensive State Restoration - Assessment state including control selection, maturity level, and name preserved",
      "🛠️ Real-time Persistence - All state changes are automatically saved as users work"
    ]
  },
  {
    date: "12/08/25",
    version: "5.6.0",
    changes: [
      "🔄 Fixed Session Persistence Issue - Control selection now persists when navigating between browser tabs",
      "📍 Enhanced State Management - activeTab state is now properly saved to localStorage and restored on page load",
      "🎯 Improved User Experience - Users maintain their position in assessments when returning from other tabs", 
      "💾 Automatic State Restoration - Assessment state including control selection is preserved across browser sessions",
      "🛠️ Enhanced Assessment Loading - Control selection is now correctly restored when loading saved assessments"
    ]
  },
  {
    date: "08/12/25",
    version: "5.5.0",
    changes: [
      "🔧 UI & MFA Setup Fixes - Fixed email overflow in user profile section for long email addresses",
      "🔐 Streamlined MFA Setup Process - Removed problematic password verification step that was causing modal closure",
      "✨ Enhanced User Experience - MFA enrollment now starts immediately when wizard opens",
      "🎯 Improved Error Handling - Better error messages and loading states during MFA setup",
      "📱 Visual Improvements - Long email addresses now truncate properly with ellipsis and hover tooltips"
    ]
  },
  {
    date: "08/12/25",
    version: "5.4.0",
    changes: [
      "🕐 Advanced Session Management - Implemented 15-minute inactivity timeout with automatic logout",
      "🔄 Activity Tracking - Mouse, keyboard, scroll, touch, and page visibility monitoring",
      "🔐 Automatic Token Refresh - Tokens refresh 5 minutes before expiration to prevent session issues", 
      "🚨 Session Timeout Warnings - Warning appears 5 minutes before timeout with extend option",
      "⚡ Page Refresh Recovery - Automatic detection and recovery from browser cache confusion"
    ]
  },
  {
    date: "08/12/25",
    version: "5.3.0",
    changes: [
      "🚀 Fixed Module Access Token Race Condition - Resolved 'No modules assigned' error for non-admin users",
      "🔄 Early Token Sync - Token synchronization now happens immediately after login",
      "🔁 Retry Logic - Added exponential backoff retry for authentication errors (1s, 2s, 4s)",
      "🎯 Enhanced Error Handling - Specific error messages and user-friendly retry options",
      "💪 Improved Reliability - Better session state management and token consistency"
    ]
  },
  {
    date: "06/08/25",
    version: "3.8.0",
    changes: [
      "🔐 Major: Implemented comprehensive Module Access Management System",
      "Added granular permission control - admins can grant/revoke access to specific modules (E8 Tracker, Reports, Crown Jewels, etc.)",
      "Enhanced User Management with interactive Permissions Matrix view",
      "Implemented real-time permission filtering - users only see modules they have access to in sidebar",
      "Added individual User Detail Modal for per-user permission management with toggle switches",
      "Created 3 new Supabase Edge Functions for secure permission API management",
      "Implemented useModuleAccess hook with real-time Supabase subscriptions",
      "Added comprehensive database schema with modules, permissions, and audit logging tables",
      "Enhanced security with Row Level Security (RLS) policies for all permission data",
      "Transition from binary admin/user model to flexible role-based access control system"
    ]
  },
  {
    date: "18/07/25",
    version: "3.7.2",
    changes: [
      "Phase 1 RLS security implementation and learning completed",
      "Implemented and tested Row Level Security policies on user_profiles table",
      "Identified and resolved RLS infinite recursion issues for learning",
      "Created comprehensive RLS learning documentation and SQL examples",
      "Enhanced database security knowledge and policy design patterns",
      "Established foundation for progressive security hardening implementation"
    ]
  },
  {
    date: "17/07/25",
    version: "3.7.1",
    changes: [
      "Fixed critical Supabase connection timeout issues affecting user management",
      "Improved circuit breaker logic for better database reliability",
      "Enhanced user authentication and approval workflow stability",
      "Added connection monitoring and diagnostic tools",
      "Optimized database query performance with smart retry strategies"
    ]
  },
  {
    date: "30/06/25",
    version: "3.7.0",
    changes: [
      "Included services matrix",
      "Included dev of ConnectSecure Integration for vulnerability management"
    ]
  },
  {
    date: "15/01/25",
    version: "3.6.0",
    changes: [
      "🎉 Crown Jewels module now available to all users without feature flags",
      "Removed ?dev=true requirement - Crown Jewels accessible from main navigation",
      "Full general availability release of Crown Jewels asset management platform",
      "Enhanced import/export functionality with 97%+ success rate",
      "Comprehensive asset review system with audit trails",
      "Visual control heatmaps and risk assessment tools",
      "Streamlined user interface with auto-save functionality",
      "Complete backwards compatibility with existing data"
    ]
  },
  {
    date: "06/06/25",
    version: "3.5.1",
    changes: [
      "Implemented URL parameter developer mode for feature access control",
      "Added ?dev=true URL parameter to enable developer-only modules (E8 Reference Tool and Crown Jewels)",
      "Hidden E8 Reference Tool and Assets section from public users - only visible with developer URL parameter",
      "Added route protection with professional 'Module Unavailable' message for restricted access",
      "Implemented developer mode indicator badge in sidebar header when dev mode is active",
      "Created developer mode utility for clean URL parameter detection and caching"
    ]
  },
  {
    date: "06/06/25",
    version: "3.5.0",
    changes: [
      "Implemented collapsible sidebar navigation for better space utilization - sidebar can now be collapsed to 64px width showing only icons",
      "Added smooth 300ms animations for sidebar collapse/expand transitions",
      "Implemented hover tooltips for collapsed sidebar icons with proper positioning",
      "Added keyboard shortcut (Ctrl/Cmd + B) to toggle sidebar state",
      "Enhanced icons: Updated to more intuitive icons (chart for E8 Tracker, lightning for E8 Light, target for QRA, trending-up for Reports, gem for Crown Jewels)",
      "Added state persistence - sidebar remembers collapsed/expanded preference between sessions",
      "Improved responsive design - main content area automatically adjusts width when sidebar is collapsed",
      "Added section dividers in collapsed state for better visual organization"
    ]
  },
  {
    date: "30/05/25",
    version: "3.4.1",
    changes: [
      "Redesigned Assessment Control Panel for streamlined workflow - consolidated assessment name, maturity level, and all action buttons into always-visible header",
      "Simplified tab structure from 4 tabs to 2 clean tabs (Overview + Progress)",
      "Removed duplicate save button from blue bar for cleaner interface",
      "Added read-only styling for assessment name after first save to prevent accidental changes",
      "Improved user experience - no more tab switching required for basic operations"
    ]
  },
  {
    date: "30/05/25",
    version: "3.4.0",
    changes: [
      "Added AutoSave functionality to the application. This will save the assessment data every 30 seconds. This is to prevent data loss in case of a power outage or other unexpected events. Major change in UI to add collapsable top bar to provide space on small screens" ,
    ]      
  },
  {
    date: "30/04/25",
    version: "3.3.2",
    changes: [
      "Added E8 Reference section to the sidebar and updated the E8 Reference page. Further updates to come. Only has Application Control and Office Macros completed so far.",
    ]
  },
  {
    date: "27/04/25",
    version: "3.3.1",
    changes: [
      "Added E8 Reference section to the sidebar and updated the E8 Reference page. Further updates to come. Only has Application Control and Office Macros completed so far.",
    ]
  },
  {
    date: "16/04/25",
    version: "3.2.0",
    changes: [
      "Updated colours in the E8 Light Assessment",
    ]
  },
  {
    date: "07/04/25",
    version: "2.7.0",
    changes: [
      "UI updates, side bar navigation, and bug fixes",
    ]
  },
  {
    date: "26/03/25",
    version: "2.6.0",
    changes: [
      "Quick Risk Assessment module added to Crown Jewels",
    ]
  },
  {
    date: "06/03/25",
    version: "2.5.0",
    changes: [
      "Crown Jewels module added",
    ]
  },
  {
    date: "24/02/25",
    version: "2.4.3",
    changes: [
      "Updated UI for better user experience",
      "Fixed up Reporting on non-applicable controls to count as complete"
    ]
  },
  {
    date: "06/02/25",
    version: "2.4.2",
    changes: [
      "Added PDF export functionality to Executive Summary report",
      "Implemented smart page breaks for better PDF formatting",
      "Added loading and success notifications for PDF generation",
      "Enhanced report section spacing and visual layout",
      "Improved chart and table rendering in PDF output",
      "Added print-specific CSS for optimal PDF formatting"
    ]
  },
  {
    date: "05/02/25",
    version: "2.4.1",
    changes: [
      "Simplified recommendations section in Executive Summary report",
      "Removed auto-generated recommendations for cleaner layout",
      "Removed implementation priority matrix and immediate action items",
      "Enhanced recommendations saving and loading functionality",
      "Improved assessment-specific recommendations persistence",
      "Fixed recommendations loading when switching between assessments",
      "Proper state management for recommendations text"
    ]
  },
  {
    date: "05/02/25",
    version: "2.4.0",
    changes: [
      "Fixed Patch Operating Systems ML2 auto-completion logic",
      "Improved ML3 compliance calculation for Patch Operating Systems",
      "Enhanced pie chart visualization for maturity level progress",
      "Updated control-specific logic for special case handling",
      "Improved compliance calculation accuracy across all controls",
      "Enhanced Overall Maturity Level calculation in reports",
      "Improved Target & Progress card visualization",
      "Updated Implementation Status calculations for better accuracy",
      "Fixed control status indicators in Executive Summary",
      "Enhanced maturity level progression display in reports"
    ]
  },
  {
    date: "19/01/25",
    version: "2.3.1",
    changes: [
      "Added recommendations tracking for each control",
      "Added summary recommendations view that updates in real-time",
      "Improved recommendations persistence between sessions",
      "Fixed recommendations clearing when creating new assessment",
      "Enhanced recommendations handling when loading saved assessments"
    ]
  },
  {
    date: "13/01/25",
    version: "2.3.0",
    changes: [
      "Added background & scope sections to reports",
      "Added ability to save assessment-specific report data",
      "Fixed assessment selection in reports module",
      "Improved data persistence between modules",
      "Added debug logging for troubleshooting"
    ]
  },
  {
    date: "12/01/25",
    version: "2.2.0",
    changes: [
      "Added assessment date picker for better date management",
      "Added new Reports module with Executive Summary, Control Assessment, and Gap Analysis",
      "Enhanced data visualization for compliance tracking",
      "Improved assessment loading and saving functionality",
      "Added dynamic date handling across the application"
    ]
  },
  {
    date: "01/01/25",
    version: "2.1.0",
    changes: [
      "Added Crown Jewels module with Vulnerability Management",
      "Enhanced data visualization for compliance tracking",
      "Improved assessment export functionality"
    ]
  }
];
